import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { createContext, useContext, useEffect } from 'react';

export const reactPlugin = new ReactPlugin();

const INSTRUMENTATION_KEY = import.meta.env.VITE_ANALYTICS_APP_INSIGHTS_INSTRUMENTATION_KEY || '';

export const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: INSTRUMENTATION_KEY,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: {},
    },
  },
});

appInsights.loadAppInsights();

const AnalyticsContext = createContext(appInsights);

/**
 * Provides the Application Insights analytics context to its children.
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render within the provider.
 * @param {object} [props.user] - The user object containing user details.
 * @returns {JSX.Element} The AnalyticsContext provider wrapping the children.
 */
export function AnalyticsProvider({ children, user }) {
  useEffect(() => {
    if (user) {
      appInsights.setAuthenticatedUserContext(user.email);
    } else {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [user]);

  return <AnalyticsContext.Provider value={appInsights}>{children}</AnalyticsContext.Provider>;
}

/**
 * Tracks a custom event using Google Analytics.
 * @param {string} action - The action name of the event.
 * @param {object} [parameters] - Additional parameters for the event.
 */
export function gtagEvent(action, parameters = {}) {
  if (globalThis.window === undefined || !globalThis.window.gtag) return;
  globalThis.window.gtag('event', action, parameters);
}

/**
 * Tracks a page view event using Google Analytics.
 * @param {string} path - The path of the page being viewed.
 * @param {string} title - The title of the page being viewed.
 * @param {string} [userId] - The user ID of the user viewing the page.
 */
export function gtagPageView(path, title, userId) {
  if (globalThis.window === 'undefined' || !globalThis.window.gtag) return;
  globalThis.window.gtag('event', 'page_view', {
    user_id: userId,
    page_path: path,
    page_title: title,
  });
}

/**
 * Custom hook to access the AnalyticsContext for Application Insights.
 * @returns {ApplicationInsights} The Application Insights instance.
 */
export function useAnalytics() {
  return useContext(AnalyticsContext);
}

/**
 * Custom hook to track events in Application Insights.
 * @returns {function(string, object): void} A function to track events with a name and optional properties.
 */
export function useTrackEvent() {
  const ai = useAnalytics();
  return (name, properties) => ai.trackEvent({ name, properties });
}
